/**
 * Centralized Audio Manager
 * Uses Web Audio API (AudioBuffer + BufferSourceNode) for reliable
 * repeated playback across all browsers including Safari.
 */

// Master volume for all sounds
const MASTER_VOLUME = 0.35;

// Web Audio API context
let audioContext: AudioContext | null = null;

// Decoded audio buffers for file-based sounds
let moveBuffer: AudioBuffer | null = null;
let diceBuffer: AudioBuffer | null = null;
let victoryBuffer: AudioBuffer | null = null;

// HTMLAudio fallback for Safari/iOS and cases when WebAudio is blocked.
let moveAudio: HTMLAudioElement | null = null;
let diceAudio: HTMLAudioElement | null = null;
let victoryAudio: HTMLAudioElement | null = null;

// Track initialization state
let isInitialized = false;
let initPromise: Promise<void> | null = null;
let unlockListenersInstalled = false;
let webAudioUnavailable = false;
let htmlAudioUnlocked = false;
const AUDIO_ENABLED_STORAGE_KEY = 'sheshdash_audio_enabled';

function readStoredAudioEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(AUDIO_ENABLED_STORAGE_KEY);
    if (stored === null) return true;
    return stored === '1';
  } catch {
    return true;
  }
}

function writeStoredAudioEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUDIO_ENABLED_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage failures.
  }
}

let userAudioEnabled = readStoredAudioEnabled();
let gameplayAudioActive = false;

function isPlaybackAllowed(): boolean {
  return userAudioEnabled && gameplayAudioActive;
}

export function setGameplayAudioActive(active: boolean): void {
  gameplayAudioActive = active;
  if (active && userAudioEnabled) {
    ensureInitialized();
    void resumeContext();
  }
}

/**
 * Get or create AudioContext (handles Safari's webkitAudioContext)
 */
function getAudioContext(): AudioContext | null {
  if (!userAudioEnabled) return null;
  if (webAudioUnavailable) return null;

  try {
    if (!audioContext) {
      const globalWithAudio = globalThis as typeof globalThis & {
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextCtor = globalWithAudio.AudioContext || globalWithAudio.webkitAudioContext;
      if (!AudioContextCtor) {
        webAudioUnavailable = true;
        return null;
      }
      audioContext = new AudioContextCtor();
    }
    return audioContext;
  } catch (error) {
    console.warn('AudioContext error:', error);
    webAudioUnavailable = true;
    audioContext = null;
    return null;
  }
}

/**
 * Resume AudioContext if suspended (required after user gesture in Safari/Chrome)
 */
async function resumeContext(): Promise<AudioContext | null> {
  const ctx = getAudioContext();
  if (!ctx) return null;

  try {
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    return ctx.state === 'running' ? ctx : null;
  } catch {
    return null;
  }
}

/**
 * Load an audio file and decode it into an AudioBuffer
 */
async function loadBuffer(url: string): Promise<AudioBuffer | null> {
  const ctx = getAudioContext();
  if (!ctx) return null;

  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await ctx.decodeAudioData(arrayBuffer);
  } catch (error) {
    console.warn('Failed to load audio buffer:', url, error);
    return null;
  }
}

/**
 * Play an AudioBuffer through the context with given volume
 */
function playBuffer(buffer: AudioBuffer | null, volume: number = MASTER_VOLUME) {
  if (!buffer || !isPlaybackAllowed()) return;

  const ctx = getAudioContext();
  if (!ctx || ctx.state !== 'running') return;

  try {
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const gainNode = ctx.createGain();
    gainNode.gain.value = volume;

    source.connect(gainNode);
    gainNode.connect(ctx.destination);
    source.start(0);
  } catch (error) {
    console.warn('Playback error:', error);
  }
}

function buildAudioElement(url: string): HTMLAudioElement {
  const audio = new Audio(url);
  audio.preload = 'auto';
  audio.volume = MASTER_VOLUME;
  return audio;
}

async function tryWarmupHTMLAudio(audio: HTMLAudioElement | null): Promise<boolean> {
  if (!audio) return false;
  const previousMuted = audio.muted;
  const previousVolume = audio.volume;
  try {
    // Safari can ignore "unlock" if media is muted. Keep it technically audible
    // but near-silent during warmup.
    audio.muted = false;
    audio.volume = 0.001;
    audio.currentTime = 0;
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    return true;
  } catch {
    // Ignore unlock warmup failures; playback will retry later.
    return false;
  } finally {
    audio.muted = previousMuted;
    audio.volume = previousVolume;
  }
}

function ensureHtmlAudioElements(base: string): void {
  if (!moveAudio) moveAudio = buildAudioElement(`${base}move.mp3`);
  if (!diceAudio) diceAudio = buildAudioElement(`${base}wooden_cube.mp3`);
  if (!victoryAudio) victoryAudio = buildAudioElement(`${base}wooden_cube.mp3`); // TODO: replace with victory.mp3
}

async function warmupHtmlAudioElements(): Promise<void> {
  const results = await Promise.all([
    tryWarmupHTMLAudio(moveAudio),
    tryWarmupHTMLAudio(diceAudio),
    tryWarmupHTMLAudio(victoryAudio),
  ]);
  if (results.some(Boolean)) {
    htmlAudioUnlocked = true;
  }
}

function playHtmlAudio(audio: HTMLAudioElement | null, volume: number = MASTER_VOLUME): void {
  if (!audio || !isPlaybackAllowed()) return;
  audio.volume = volume;
  try {
    audio.currentTime = 0;
  } catch {
    // Safari may throw if currentTime can't be set yet; playback attempt below can still work.
  }

  void audio.play().catch(() => {
    // iOS Safari is often stricter for dynamically created elements.
    // Fallback to clone as a secondary attempt.
    try {
      const clone = audio.cloneNode(true) as HTMLAudioElement;
      clone.volume = volume;
      void clone.play().catch(() => {
        // Ignore blocked autoplay attempts.
      });
    } catch {
      // Ignore fallback playback errors.
    }
  });
}

/**
 * Initialize all audio systems — call on first user interaction
 */
export async function initAudio() {
  if (!userAudioEnabled) return;
  if (initPromise) {
    await initPromise;
    return;
  }

  initPromise = (async () => {
    const base = import.meta.env.BASE_URL;
    try {
      // HTMLAudio fallback must be available even when AudioContext can't be created (Safari/iOS).
      ensureHtmlAudioElements(base);
      // HTMLAudio warmup is performed exclusively inside `handleUnlockGesture`
      // (which is gated to trusted user-gesture events). Calling
      // `audio.play()` from a non-gesture path leaks audible playback on
      // browsers that preserve user-activation across page reloads, even at
      // `volume = 0.001`.

      const ctx = getAudioContext();
      if (ctx) {
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        // Decode WebAudio buffers lazily; retry missing ones on subsequent init calls.
        if (!moveBuffer || !diceBuffer || !victoryBuffer) {
          const [move, dice, victory] = await Promise.all([
            moveBuffer ? Promise.resolve(moveBuffer) : loadBuffer(`${base}move.mp3`),
            diceBuffer ? Promise.resolve(diceBuffer) : loadBuffer(`${base}wooden_cube.mp3`),
            victoryBuffer ? Promise.resolve(victoryBuffer) : loadBuffer(`${base}wooden_cube.mp3`), // TODO: replace with victory.mp3
          ]);

          moveBuffer = moveBuffer ?? move;
          diceBuffer = diceBuffer ?? dice;
          victoryBuffer = victoryBuffer ?? victory;
        }
      }

      // Mark initialized if at least one source (WebAudio or HTMLAudio) is ready.
      isInitialized = Boolean(moveAudio || diceAudio || victoryAudio || moveBuffer || diceBuffer || victoryBuffer);
      if (!isInitialized) {
        console.warn('Audio initialization skipped: no available sources');
      }
    } catch (error) {
      console.warn('Audio initialization error:', error);
    } finally {
      initPromise = null;
    }
  })();

  await initPromise;
}

function ensureInitialized() {
  if (!userAudioEnabled) return;
  if (!isInitialized && !initPromise) {
    void initAudio();
  }
}

/**
 * Play move sound
 */
export function playMoveSound() {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  if (!moveAudio || !diceAudio || !victoryAudio) {
    ensureHtmlAudioElements(import.meta.env.BASE_URL);
  }

  const ctx = getAudioContext();
  if (ctx?.state === 'running' && moveBuffer) {
    playBuffer(moveBuffer);
    return;
  }
  playHtmlAudio(moveAudio);
  void resumeContext();
}

/**
 * Play dice roll sound
 */
export function playDiceRollSound() {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  if (!moveAudio || !diceAudio || !victoryAudio) {
    ensureHtmlAudioElements(import.meta.env.BASE_URL);
  }

  const ctx = getAudioContext();
  if (ctx?.state === 'running' && diceBuffer) {
    playBuffer(diceBuffer);
    return;
  }
  playHtmlAudio(diceAudio);
  void resumeContext();
}

/**
 * Play victory sound (louder)
 */
export function playVictorySound() {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  if (!moveAudio || !diceAudio || !victoryAudio) {
    ensureHtmlAudioElements(import.meta.env.BASE_URL);
  }

  const ctx = getAudioContext();
  if (ctx?.state === 'running' && victoryBuffer) {
    playBuffer(victoryBuffer, 0.6);
    return;
  }
  playHtmlAudio(victoryAudio, 0.6);
  void resumeContext();
}

/**
 * Play capture sound - synthesized explosion (bass thump)
 */
export function playCaptureSound() {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  const ctx = getAudioContext();
  if (!ctx) {
    if (!moveAudio || !diceAudio || !victoryAudio) {
      ensureHtmlAudioElements(import.meta.env.BASE_URL);
    }
    playHtmlAudio(moveAudio, MASTER_VOLUME * 0.7);
    void resumeContext();
    return;
  }

  try {
    const now = ctx.currentTime;

    const bass = ctx.createOscillator();
    bass.type = 'sine';
    bass.frequency.setValueAtTime(120, now);
    bass.frequency.exponentialRampToValueAtTime(30, now + 0.2);

    const bassGain = ctx.createGain();
    bassGain.gain.setValueAtTime(MASTER_VOLUME * 0.8, now);
    bassGain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

    bass.connect(bassGain);
    bassGain.connect(ctx.destination);

    bass.start(now);
    bass.stop(now + 0.2);
  } catch (error) {
    console.warn('Capture sound error:', error);
    playHtmlAudio(moveAudio, MASTER_VOLUME * 0.7);
  }
}

/**
 * Play blink warning sound - simple beep
 */
export function playBlinkSound() {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  const ctx = getAudioContext();
  if (!ctx) {
    if (!moveAudio || !diceAudio || !victoryAudio) {
      ensureHtmlAudioElements(import.meta.env.BASE_URL);
    }
    playHtmlAudio(diceAudio, MASTER_VOLUME * 0.25);
    void resumeContext();
    return;
  }

  try {
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(MASTER_VOLUME * 0.25, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.08);
  } catch (error) {
    console.warn('Blink sound error:', error);
    playHtmlAudio(diceAudio, MASTER_VOLUME * 0.25);
  }
}

/**
 * Play tick sound for timer countdown - short high beep.
 * Final tick (1s) is longer and higher.
 */
export function playTickSound(remaining: number) {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  const ctx = getAudioContext();
  if (!ctx) {
    if (!moveAudio || !diceAudio || !victoryAudio) {
      ensureHtmlAudioElements(import.meta.env.BASE_URL);
    }
    playHtmlAudio(diceAudio, MASTER_VOLUME * 0.2);
    void resumeContext();
    return;
  }

  try {
    const now = ctx.currentTime;
    const isFinal = remaining === 1;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    // Fixed pitch tick; final tick differs by duration only.
    osc.frequency.setValueAtTime(1000, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(MASTER_VOLUME * 0.3, now);
    
    // Final tick: 0.3s duration, Normal: 0.05s
    const duration = isFinal ? 0.3 : 0.05;
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + duration);
  } catch (error) {
    console.warn('Tick sound error:', error);
    playHtmlAudio(diceAudio, MASTER_VOLUME * 0.2);
  }
}

/**
 * Play festive piece placement sound - ascending major chord
 */
export function playPlaceSound() {
  if (!isPlaybackAllowed()) return;
  ensureInitialized();
  const ctx = getAudioContext();
  if (!ctx) {
    if (!moveAudio || !diceAudio || !victoryAudio) {
      ensureHtmlAudioElements(import.meta.env.BASE_URL);
    }
    playHtmlAudio(moveAudio, MASTER_VOLUME * 0.8);
    void resumeContext();
    return;
  }

  try {
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99]; // C5 -> E5 -> G5
    const noteDuration = 0.08;

    notes.forEach((freq, index) => {
      const startTime = now + index * noteDuration;

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(MASTER_VOLUME * 0.35, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + noteDuration * 2);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + noteDuration * 2);
    });
  } catch (error) {
    console.warn('Place sound error:', error);
    playHtmlAudio(moveAudio, MASTER_VOLUME * 0.8);
  }
}

async function handleUnlockGesture() {
  // Only gate on user preference — NOT gameplayAudioActive. AudioContext must
  // be resumed during a user gesture (especially iOS Safari). If we wait until
  // gameplay starts, the gesture opportunity is lost and audio stays suspended.
  if (!userAudioEnabled) return;

  try {
    if (!audioContext) {
      const globalWithAudio = globalThis as typeof globalThis & {
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextCtor = globalWithAudio.AudioContext || globalWithAudio.webkitAudioContext;
      if (AudioContextCtor) {
        audioContext = new AudioContextCtor();
      }
    }
    if (audioContext && audioContext.state === 'suspended') {
      await audioContext.resume();
    }
  } catch {
    // Retry on next gesture.
  }

  // Pre-load HTML audio elements so they're unlocked too.
  if (!moveAudio || !diceAudio || !victoryAudio) {
    ensureHtmlAudioElements(import.meta.env.BASE_URL);
  }
  if (!htmlAudioUnlocked) {
    await warmupHtmlAudioElements();
  }
}

/**
 * Public unlock entry — call from a user-gesture handler (button onClick) to
 * kick the AudioContext out of "suspended" within the gesture window. iOS
 * Safari is strict here: a resume() scheduled from a useEffect that runs
 * after several async hops (click → WS → server → setState → effect) is
 * past the gesture deadline and silently fails. Document-level bubble
 * listeners installed by installAudioUnlockListeners are also unreliable on
 * iOS — Safari sometimes won't treat them as trusted. So for actions known
 * to follow long inactivity (resume after pause, version-banner refresh,
 * etc.) call unlockAudio() directly from the click handler.
 */
export function unlockAudio(): void {
  void handleUnlockGesture();
}

/**
 * Install resilient unlock listeners for Safari/iOS.
 */
export function installAudioUnlockListeners() {
  if (unlockListenersInstalled || typeof document === 'undefined') return;
  unlockListenersInstalled = true;

  const events: Array<keyof DocumentEventMap> = ['touchstart', 'touchend', 'pointerdown', 'mousedown', 'keydown', 'click'];
  const onGesture = () => {
    void handleUnlockGesture();
  };
  events.forEach((eventName) => {
    document.addEventListener(eventName, onGesture, { passive: true });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void handleUnlockGesture();
    }
  });
}

export function isAudioEnabled(): boolean {
  return userAudioEnabled;
}

export function setAudioEnabled(enabled: boolean): void {
  userAudioEnabled = enabled;
  writeStoredAudioEnabled(enabled);

  if (!enabled) {
    [moveAudio, diceAudio, victoryAudio].forEach((audio) => {
      if (!audio) return;
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        // Ignore pause/reset issues.
      }
    });
    return;
  }

  void initAudio();
}
